import React, { useState, useEffect, useCallback } from 'react';
import { courtsApi } from '../../api';
import { SmartphoneIcon, ClipboardIcon, CheckIcon } from '../icons';

export default function KioskDeviceManager({ courts }) {
  const [devices, setDevices]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [newName, setNewName]     = useState('');
  const [newCourt, setNewCourt]   = useState('');
  const [creating, setCreating]   = useState(false);
  const [copied, setCopied]       = useState(null);

  const load = useCallback(async () => {
    try { const d = await courtsApi.kioskDevices(); setDevices(d.results || d); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await courtsApi.kioskDeviceCreate({ device_name: newName.trim(), court: newCourt || null });
      setNewName(''); setNewCourt(''); load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to create device'); }
    finally { setCreating(false); }
  };

  const handleAssign = async (id, courtId) => {
    try { await courtsApi.kioskDeviceUpdate(id, { court: courtId || null }); load(); }
    catch { alert('Failed to update'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await courtsApi.kioskDeviceDelete(id); load(); }
    catch { alert('Failed to delete'); }
  };

  const copyToken = (token, id) => {
    navigator.clipboard.writeText(token);
    setCopied(id); setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div>
      {/* Add device form */}
      <div style={s.addCard}>
        <h3 style={s.addTitle}>Add New Kiosk Device</h3>
        <p style={s.addSub}>Create a device entry, copy its token, then paste it on the kiosk to activate it.</p>
        <div style={s.addRow}>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Device name, e.g. iPad Court 1"
            style={s.input}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
          <select value={newCourt} onChange={e => setNewCourt(e.target.value)} style={s.select}>
            <option value="">— Assign court (optional) —</option>
            {courts.map(c => (
              <option key={c.id} value={c.id}>{c.name || `Court ${c.number}`}</option>
            ))}
          </select>
          <button
            onClick={handleCreate}
            disabled={!newName.trim() || creating}
            style={{ ...s.addBtn, opacity: (!newName.trim() || creating) ? 0.55 : 1 }}
          >
            {creating ? 'Creating…' : '+ Add Device'}
          </button>
        </div>
      </div>

      {/* Device list */}
      <div style={s.listCard}>
        <div style={s.listHeader}>
          <h3 style={s.listTitle}>Registered Devices</h3>
          <span style={s.listCount}>{devices.length} device{devices.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div style={s.empty}>Loading…</div>
        ) : devices.length === 0 ? (
          <div style={s.empty}>No devices yet. Add one above.</div>
        ) : (
          <div style={s.list}>
            {devices.map(device => (
              <div key={device.id} style={s.deviceRow}>
                <div style={s.deviceLeft}>
                  <div style={s.deviceIcon}><SmartphoneIcon size={18} /></div>
                  <div>
                    <div style={s.deviceName}>{device.device_name}</div>
                    <div style={s.deviceMeta}>
                      {device.last_seen
                        ? `Last seen ${new Date(device.last_seen).toLocaleString()}`
                        : 'Never connected'}
                    </div>
                  </div>
                </div>

                <div style={s.deviceRight}>
                  <select
                    value={device.court || ''}
                    onChange={e => handleAssign(device.id, e.target.value)}
                    style={s.assignSelect}
                  >
                    <option value="">Unassigned</option>
                    {courts.map(c => (
                      <option key={c.id} value={c.id}>{c.name || `Court ${c.number}`}</option>
                    ))}
                  </select>

                  <button
                    onClick={() => copyToken(device.device_token, device.id)}
                    style={s.tokenBtn}
                  >
                    {copied === device.id
                    ? <><CheckIcon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Copied</>
                    : <><ClipboardIcon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Copy Token</>}
                  </button>

                  <button
                    onClick={() => handleDelete(device.id, device.device_name)}
                    style={s.deleteBtn}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  addCard: {
    background: 'var(--white)', borderRadius: 'var(--radius)',
    border: '1px solid var(--gray-200)', padding: '24px',
    boxShadow: 'var(--shadow-sm)', marginBottom: 16,
  },
  addTitle: { fontSize: 16, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 4 },
  addSub: { fontSize: 13, color: 'var(--gray-500)', marginBottom: 16, lineHeight: 1.5 },
  addRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  input: {
    flex: 2, minWidth: 180, padding: '10px 13px',
    border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-sm)',
    fontSize: 14, outline: 'none', fontFamily: 'var(--font)',
    color: 'var(--gray-900)', background: 'var(--white)',
  },
  select: {
    flex: 1, minWidth: 160, padding: '10px 13px',
    border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-sm)',
    fontSize: 14, outline: 'none', background: 'var(--white)',
    fontFamily: 'var(--font)', color: 'var(--gray-900)',
  },
  addBtn: {
    background: 'var(--blue)', color: 'white',
    border: 'none', borderRadius: 'var(--radius-sm)',
    padding: '10px 20px', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font)',
  },
  listCard: {
    background: 'var(--white)', borderRadius: 'var(--radius)',
    border: '1px solid var(--gray-200)', overflow: 'hidden',
    boxShadow: 'var(--shadow-sm)',
  },
  listHeader: {
    padding: '16px 20px', borderBottom: '1px solid var(--gray-100)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  listTitle: { fontSize: 15, fontWeight: 700, color: 'var(--gray-900)' },
  listCount: { fontSize: 13, color: 'var(--gray-400)', fontWeight: 500 },
  empty: { padding: '48px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column' },
  deviceRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', borderBottom: '1px solid var(--gray-100)',
    gap: 16, flexWrap: 'wrap',
  },
  deviceLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  deviceIcon: {
    width: 36, height: 36, borderRadius: 'var(--radius-sm)',
    background: 'var(--gray-100)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
  },
  deviceName: { fontSize: 14, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 2 },
  deviceMeta: { fontSize: 12, color: 'var(--gray-400)' },
  deviceRight: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  assignSelect: {
    padding: '7px 11px', border: '1.5px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)', fontSize: 13,
    background: 'var(--white)', outline: 'none',
    fontFamily: 'var(--font)', color: 'var(--gray-700)',
  },
  tokenBtn: {
    background: 'var(--blue-light)', color: 'var(--blue)',
    border: '1px solid rgba(26,107,255,0.2)',
    borderRadius: 'var(--radius-sm)', padding: '7px 13px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'var(--font)', whiteSpace: 'nowrap',
  },
  deleteBtn: {
    background: 'var(--red-light)', color: 'var(--red)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 'var(--radius-sm)', padding: '7px 13px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'var(--font)',
  },
};
